/**
 * Account page › Price books (Callum, 2026-09-25: "we should be showing price books we have
 * created in the pricing system, against customer accounts").
 *
 * Pricing owns the books; this shows the account's OWN customer books (Farnell's, Motec's) —
 * the prices agreed with THIS customer. Opening one lists its parts and price breaks,
 * searchable, each part linked to the Part view priced for this account.
 * ⚠️ The general books for the division (web shop, stocked-item list, pricing's test books)
 * are deliberately NOT shown (Callum, 2026-09-25): they are not agreed with this customer, and
 * beside the customer's own book they read as its prices.
 * - Every book is labelled in ITS OWN currency (pricing DO #72).
 * - Prices are per the book's price unit (KM, HP…) with the sell unit beside it when they
 *   differ — the same two views pricing's own Prices tab shows (DO #27).
 * - Each book's lines come with the VARIANCE against its previous version and our COST and
 *   MARGIN — all worked out by pricing (its engine's own cost rule); this only displays them.
 *   A grant with margin switched off gets neither (stripped server-side).
 */
import { debounce, escapeHtml as esc, getIcon } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { partUrl } from '../lib/links.js';
import { shortDate } from '../lib/fig.js';

const SYMBOL = { GBP: '£', USD: '$', EUR: '€' };
function price(v, ccy) {
  if (v == null) return '—';
  const digits = v < 1 ? 4 : 2;
  return `${SYMBOL[ccy] || (ccy ? `${ccy} ` : '')}${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: digits })}`;
}

export async function loadPriceBooks(account) {
  const res = await salesApi.getPriceBooks(account.entity, account.code);
  return { items: (res.data || {}).items, source: (((res.meta || {}).sources) || {}).pricing };
}

export function renderPriceBooks(panel, account, { items, source }) {
  if (!items) {
    panel.innerHTML = `<div class="card card-body" style="color:var(--text-tertiary)">${getIcon('alert-circle', 14)}
      Pricing did not answer, so this account's price books cannot be shown just now${source && source.reason ? ` (${esc(source.reason)})` : ''}.</div>`;
    return;
  }
  const mine = items.filter(b => b.scope === 'customer');
  const card = (b) => `
    <div class="sc-acc-row" data-book="${b.id}" aria-expanded="false" role="button" tabindex="0">
      <span class="sc-acc-caret">${getIcon('chevron-right', 14)}</span>
      <div style="flex:1;min-width:0">
        <strong>${esc(b.name)}</strong>
        <span class="badge badge-gray">${esc(b.currency || '')}</span>
        ${b.in_date === false
          ? `<span class="badge badge-gray" title="Out of date, so quotes do not use it">${b.expiry_date ? `expired ${esc(shortDate(b.expiry_date))}` : 'not in date'}</span>`
          : b.in_date ? '<span class="badge badge-green" title="Quotes for this account are priced from this book for the parts it covers (a win-back strategy is the exception)">sets quote prices</span>' : ''}
        ${b.description ? `<div class="sc-list-meta">${esc(b.description)}</div>` : ''}
      </div>
      <div class="sc-list-meta" style="text-align:right;white-space:nowrap">${b.priced} of ${b.parts} parts priced
        ${b.updated_at ? `<br>prices changed ${esc(shortDate(b.updated_at))}` : ''}</div>
    </div>
    <div class="sc-acc-body" data-book-body="${b.id}" hidden></div>`;
  panel.innerHTML = `
    <p class="sc-list-meta" style="margin:0 0 var(--space-3)">The price books agreed with this account, from the
      pricing system — each with how it changed since its previous version, and our cost and margin.</p>
    <div class="card" style="padding:0;overflow:hidden">
      ${mine.length ? mine.map(card).join('') : `<div class="card-body sc-list-meta">No price book is set up for this account.
        Pricing can make one (Price books › Import from QX, or a book for this customer).</div>`}
    </div>`;

  panel.querySelectorAll('[data-book]').forEach(row => {
    const toggle = () => {
      const body = panel.querySelector(`[data-book-body="${row.dataset.book}"]`);
      const open = row.getAttribute('aria-expanded') === 'true';
      row.setAttribute('aria-expanded', open ? 'false' : 'true');
      body.hidden = open;
      if (!open && !body.dataset.loaded) openBook(body, items.find(b => String(b.id) === row.dataset.book));
    };
    row.addEventListener('click', toggle);
    row.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
  });

  async function openBook(body, book) {
    body.innerHTML = '<div class="spinner" style="margin:var(--space-4) auto"></div>';
    let lines;
    try {
      lines = ((await salesApi.getPriceBookLines(account.entity, account.code, book.id)).data || {}).lines;
    } catch (e) {
      body.innerHTML = `<p class="sc-list-meta">${esc(e.message || 'The book could not be opened.')}</p>`;
      return;
    }
    body.dataset.loaded = '1';
    const all = (lines && lines.items) || [];
    const prev = lines && lines.previous;
    const sm = (lines && lines.summary) || {};
    const pct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
    // pricing's own margin convention (its recommendations page): thin under 15% red, fat over
    // 45% amber, below cost red and bold, "was x% ▲/▼" underneath
    const marginCell = (b, cost) => {
      const m = b.margin_pct;
      if (m == null) return '<span class="sc-list-meta" title="No buy cost on record for this part">—</span>';
      let color = 'var(--text-secondary)', tip = `Margin at ${price(b.current_price_display ?? b.current_price, book.currency)} against our cost ${price(cost, book.currency)}`;
      if (m < 0) { color = 'var(--red-600,#dc2626)'; tip += ' — BELOW COST'; }
      else if (m < 15) { color = 'var(--red-600,#dc2626)'; tip += ' — thin (<15%)'; }
      else if (m > 45) { color = 'var(--yellow-700,#a16207)'; tip += ' — fat (>45%)'; }
      const was = b.previous_margin_pct;
      let under = '';
      if (was != null) {
        const d = m - was;
        const arrow = Math.abs(d) < 0.05 ? '' : d > 0 ? '<span style="color:var(--green-600,#059669)">▲</span>' : '<span style="color:var(--red-500,#ef4444)">▼</span>';
        tip += ` · was ${was.toFixed(1)}% in the previous version`;
        under = `<div style="font-size:var(--text-xs);color:var(--text-tertiary);white-space:nowrap">was ${was.toFixed(1)}% ${arrow}</div>`;
      }
      return `<div title="${esc(tip)}"><span style="font-variant-numeric:tabular-nums;font-weight:${m < 0 ? 700 : 500};color:${color}">${m < 0 ? 'below cost ' : ''}${m.toFixed(1)}%</span>${under}</div>`;
    };
    const changeCell = (b) => {
      if (b.change === 'new') return '<span class="badge badge-blue" title="Not in the previous version">new</span>';
      if (b.change === 'up') return `<span style="color:var(--green-700,#047857);font-variant-numeric:tabular-nums">▲ ${b.change_pct}%</span>
        <div style="font-size:var(--text-xs);color:var(--text-tertiary)">was ${esc(price(b.previous_price_display ?? b.previous_price, book.currency))}</div>`;
      if (b.change === 'down') return `<span style="color:var(--red-600,#dc2626);font-variant-numeric:tabular-nums">▼ ${Math.abs(b.change_pct)}%</span>
        <div style="font-size:var(--text-xs);color:var(--text-tertiary)">was ${esc(price(b.previous_price_display ?? b.previous_price, book.currency))}</div>`;
      if (b.change === 'same') return '<span class="sc-list-meta">—</span>';
      return '';
    };
    const has = (l, f) => l.breaks.some(f);
    const FILTERS = {
      all: () => true,
      changed: (l) => has(l, b => b.change === 'up' || b.change === 'down' || b.change === 'new'),
      down: (l) => has(l, b => b.change === 'down'),
      up: (l) => has(l, b => b.change === 'up'),
      new: (l) => has(l, b => b.change === 'new'),
      below: (l) => has(l, b => b.margin_pct != null && b.margin_pct < 0),
      unpriced: (l) => !l.breaks.length,
    };
    const showCost = all.some(l => 'cost' in l);
    const num = 'text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap';
    // ONE ROW PER PRICE BREAK, the part named once; every fact in its OWN column (Callum):
    // break · price · the unit the price is per · sell / buy units · pack size · MSQ · change · cost · margin
    const qtyFmt = (v) => (v == null || Number(v) === 0 ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 3 }));
    // pricing's "description" is often just QX's item code, or the part number again — not a description
    const realDesc = (l) => (l.description && !/^\d+$/.test(l.description.trim())
      && l.description.replace(/[^A-Z0-9]/gi, '').toUpperCase() !== l.part_number.replace(/[^A-Z0-9]/gi, '').toUpperCase()
      ? l.description : null);
    const u = (v) => (v ? esc(v) : '<span class="sc-list-meta">—</span>');
    const table = (rows) => rows.length ? `<div style="overflow-x:auto"><table class="data-table sc-book-table"><thead><tr>
        <th>Part</th><th style="text-align:right">Qty break</th><th style="text-align:right">Price</th>
        ${showCost ? '<th style="text-align:right">Margin</th>' : ''}${prev ? '<th>vs previous</th>' : ''}
        ${showCost ? '<th style="text-align:right" title="Per the price unit, as pricing margins the book">Our cost</th>' : ''}
        <th>Price per</th><th>Sell UOM</th><th>Buy UOM</th><th style="text-align:right">Pack size</th><th style="text-align:right">MSQ</th>
        </tr></thead><tbody>${rows.map(l => {
          const span = Math.max(l.breaks.length, 1);
          const top = 'vertical-align:top';
          const partCells = `<td rowspan="${span}" style="${top};white-space:nowrap"><a href="${partUrl(l.part_number, account.entity, { customer: account.code })}" data-link>${esc(l.part_number)}</a>
              ${realDesc(l) ? `<div class="sc-list-meta" style="max-width:260px;white-space:normal">${esc(realDesc(l))}</div>` : ''}</td>`;
          const unitCells = `<td rowspan="${span}" style="${top}">${u(l.uom_price)}</td><td rowspan="${span}" style="${top}">${u(l.uom_sell)}</td>
              <td rowspan="${span}" style="${top}">${u(l.uom_buy)}</td>
              <td rowspan="${span}" style="${num};${top}">${qtyFmt(l.pack_size)}</td><td rowspan="${span}" style="${num};${top}">${qtyFmt(l.msq)}</td>`;
          const costCell = showCost ? `<td rowspan="${span}" style="${num};${top}">${l.cost_display != null || l.cost != null
            ? esc(price(l.cost_display ?? l.cost, book.currency)) : '<span class="sc-list-meta" title="No buy cost on record">—</span>'}</td>` : '';
          if (!l.breaks.length) {
            return `<tr>${partCells}<td colspan="${2 + (showCost ? 1 : 0) + (prev ? 1 : 0)}" style="color:var(--amber-700,#b45309)">no price yet</td>
              ${costCell}${unitCells}</tr>`;
          }
          return l.breaks.map((b, i) => `<tr>
            ${i === 0 ? partCells : ''}
            <td style="${num}">${qtyFmt(b.break_qty)}+</td>
            <td style="${num};font-weight:600">${esc(price(b.current_price_display ?? b.current_price, book.currency))}</td>
            ${showCost ? `<td style="text-align:right">${marginCell(b, l.cost_display ?? l.cost)}</td>` : ''}
            ${prev ? `<td style="white-space:nowrap">${changeCell(b)}</td>` : ''}
            ${showCost && i === 0 ? costCell : ''}
            ${i === 0 ? unitCells : ''}
          </tr>`).join('');
        }).join('')}</tbody></table></div>`
      : '<p class="sc-list-meta">No parts match.</p>';
    const count = (k) => all.filter(FILTERS[k]).length;
    const tile = (label, value, hint, color) => `<div class="card card-body" style="padding:var(--space-3)">
        <div class="stat-card-label">${label}</div><div class="stat-card-value" style="font-size:var(--text-xl)${color ? `;color:${color}` : ''}">${value}</div>
        ${hint ? `<div class="sc-list-meta">${hint}</div>` : ''}</div>`;
    const mTone = (m) => (m == null ? '' : m < 15 ? 'var(--red-600,#dc2626)' : m > 45 ? 'var(--yellow-700,#a16207)' : '');
    body.innerHTML = `
      <p class="sc-list-meta" style="margin:0 0 var(--space-2)">${prev
        ? `Compared with the previous version — v${prev.version_no}${prev.label ? ` “${esc(prev.label)}”` : ''}${prev.created_at ? `, ${esc(shortDate(prev.created_at))}` : ''}.`
        : 'No earlier version of this book to compare against.'}</p>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:var(--space-2);margin-bottom:var(--space-3)">
        ${prev ? `${tile('Prices down', sm.down || 0, '', sm.down ? 'var(--red-600,#dc2626)' : '')}
          ${tile('Prices up', sm.up || 0, '', sm.up ? 'var(--green-700,#047857)' : '')}
          ${tile('New since', sm.new || 0, `${sm.removed || 0} removed`)}` : ''}
        ${sm.avg_margin_pct != null ? tile('Average margin', pct(sm.avg_margin_pct),
          sm.avg_previous_margin_pct != null ? `was ${pct(sm.avg_previous_margin_pct)} ${sm.avg_margin_pct > sm.avg_previous_margin_pct ? '▲' : sm.avg_margin_pct < sm.avg_previous_margin_pct ? '▼' : ''}` : '', mTone(sm.avg_margin_pct)) : ''}
        ${sm.below_cost != null ? tile('Below cost', sm.below_cost, 'price breaks', sm.below_cost ? 'var(--red-600,#dc2626)' : '') : ''}
      </div>
      <div style="display:flex;gap:var(--space-2);flex-wrap:wrap;margin-bottom:var(--space-2)">
        <input type="text" class="form-input" data-book-q placeholder="Find a part in this book…" style="max-width:300px">
        <select class="form-select" data-book-f style="width:auto">
          <option value="all">All parts (${all.length})</option>
          ${prev ? `<option value="changed">Changed (${count('changed')})</option><option value="down">Price down (${count('down')})</option>
            <option value="up">Price up (${count('up')})</option><option value="new">New since (${count('new')})</option>` : ''}
          ${showCost ? `<option value="below">Below cost (${count('below')})</option>` : ''}
          <option value="unpriced">No price yet (${count('unpriced')})</option>
        </select></div>
      <div data-book-rows>${table(all.slice(0, 200))}</div>
      <p class="sc-list-meta" data-book-foot>${all.length > 200 ? `Showing 200 of ${all.length} — search or filter to find the rest.` : `${all.length} parts.`}
        ${lines && lines.truncated ? ' The book is larger than this view shows.' : ''}${showCost
          ? ' Cost and margin are what pricing margins this book against.' : ''}</p>`;
    const q = body.querySelector('[data-book-q]');
    const f = body.querySelector('[data-book-f]');
    const redraw = () => {
      const needle = q.value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      const rows = all.filter(FILTERS[f.value] || FILTERS.all)
        .filter(l => !needle || l.part_number.toUpperCase().replace(/[^A-Z0-9]/g, '').includes(needle));
      body.querySelector('[data-book-rows]').innerHTML = table(rows.slice(0, 200));
      body.querySelector('[data-book-foot]').textContent = rows.length > 200 ? `Showing 200 of ${rows.length}.` : `${rows.length} parts.`;
    };
    [q, f].forEach(el => el.addEventListener('click', (e) => e.stopPropagation()));
    q.addEventListener('input', debounce(redraw, 200));
    f.addEventListener('change', redraw);
  }
}
